/**
 * Resume Service
 * Handles resume CRUD operations and auto-save functionality
 */

import { doc, getDoc, setDoc, updateDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

/**
 * Load a resume from Firestore
 * @param {string} userId - User ID
 * @param {string} resumeId - Resume document ID
 * @returns {Promise<object|null>} Resume data or null if not found
 */
export const loadResume = async (userId, resumeId) => {
  try {
    console.log('📂 Loading resume:', { userId, resumeId });
    
    const resumeRef = doc(db, 'users', userId, 'resumes', resumeId);
    const resumeSnap = await getDoc(resumeRef);
    
    if (resumeSnap.exists()) {
      const data = resumeSnap.data();
      console.log('✅ Resume loaded successfully');
      return {
        id: resumeSnap.id,
        ...data
      };
    } else {
      console.warn('⚠️ Resume not found');
      return null;
    }
  } catch (error) {
    console.error('❌ Error loading resume:', error);
    throw error;
  }
};

/**
 * Create a new resume document in Firestore
 * @param {string} userId - User ID
 * @param {string} templateId - Template ID used for this resume
 * @param {string} title - Resume title
 * @returns {Promise<string>} New resume document ID
 */
export const createResume = async (userId, templateId, title = 'Untitled Resume') => {
  try {
    console.log('🆕 Creating new resume:', { userId, templateId, title });
    
    // Generate a unique ID for the resume
    const resumeId = `resume_${Date.now()}`;
    const resumeRef = doc(db, 'users', userId, 'resumes', resumeId);
    
    const initialData = {
      title,
      templateId,
      data: {
        personalInfo: {
          name: '',
          email: '',
          phone: '',
          location: '',
          linkedin: '',
          website: ''
        },
        summary: '',
        experience: [],
        education: [],
        skills: [],
        projects: [],
        certifications: []
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(resumeRef, initialData);
    console.log('✅ Resume created successfully:', resumeId);
    
    return resumeId;
  } catch (error) {
    console.error('❌ Error creating resume:', error);
    throw error;
  }
};

/**
 * Save resume data to Firestore
 * @param {string} userId - User ID
 * @param {string} resumeId - Resume document ID
 * @param {object} resumeData - Resume data to save (can include data, title, templateId, etc.)
 * @returns {Promise<void>}
 */
export const saveResume = async (userId, resumeId, resumeData) => {
  try {
    console.log('💾 Saving resume:', { userId, resumeId });
    
    const resumeRef = doc(db, 'users', userId, 'resumes', resumeId);
    
    // Support both direct data object and structured object with metadata
    const updateData = resumeData.data ? {
      // Structured format: { data, title, templateId, etc. }
      ...resumeData,
      updatedAt: serverTimestamp()
    } : {
      // Legacy format: just the data object
      data: resumeData,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(resumeRef, updateData);
    
    console.log('✅ Resume saved successfully');
  } catch (error) {
    // If document doesn't exist, create it
    if (error.code === 'not-found') {
      console.log('📝 Document not found, creating new one...');
      const resumeRef = doc(db, 'users', userId, 'resumes', resumeId);
      
      const createData = resumeData.data ? {
        // Structured format
        ...resumeData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      } : {
        // Legacy format
        data: resumeData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await setDoc(resumeRef, createData);
    } else {
      console.error('❌ Error saving resume:', error);
      throw error;
    }
  }
};

/**
 * Delete a resume from Firestore
 * @param {string} userId - User ID
 * @param {string} resumeId - Resume document ID
 * @returns {Promise<void>}
 */
export const deleteResume = async (userId, resumeId) => {
  try {
    console.log('🗑️ Deleting resume:', { userId, resumeId });
    
    const resumeRef = doc(db, 'users', userId, 'resumes', resumeId);
    await deleteDoc(resumeRef);
    
    console.log('✅ Resume deleted successfully');
  } catch (error) {
    console.error('❌ Error deleting resume:', error);
    throw error;
  }
};

/**
 * AutoSave class for debounced saving
 * Automatically saves resume data after a delay when changes are made
 */
export class AutoSave {
  constructor(userId, resumeId, debounceMs = 2000) {
    this.userId = userId;
    this.resumeId = resumeId;
    this.debounceMs = debounceMs;
    this.timeoutId = null;
    this.pendingData = null;
    this.isSaving = false;
    
    console.log('🔄 AutoSave initialized:', { userId, resumeId, debounceMs });
  }
  
  /**
   * Queue data to be saved after debounce delay
   * @param {object} data - Resume data to save
   */
  save(data) {
    // Store the latest data
    this.pendingData = data;
    
    // Clear existing timeout
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    
    // Set new timeout
    this.timeoutId = setTimeout(() => {
      this.flush();
    }, this.debounceMs);
  }
  
  /**
   * Immediately save pending data
   * @returns {Promise<void>}
   */
  async flush() {
    // Clear timeout
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    
    // Save if there's pending data and not currently saving
    if (this.pendingData && !this.isSaving) {
      this.isSaving = true;
      
      try {
        await saveResume(this.userId, this.resumeId, this.pendingData);
        this.pendingData = null;
      } catch (error) {
        console.error('❌ AutoSave flush error:', error);
        // Keep pending data for retry
      } finally {
        this.isSaving = false;
      }
    }
  }
  
  /**
   * Cancel pending save and cleanup
   */
  cancel() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.pendingData = null;
    console.log('🚫 AutoSave canceled');
  }
  
  /**
   * Check if there's pending data to save
   * @returns {boolean}
   */
  hasPendingChanges() {
    return this.pendingData !== null;
  }
}

/**
 * List all resumes for a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of resume objects
 */
export const listResumes = async (userId) => {
  try {
    console.log('📋 Listing resumes for user:', userId);
    
    const resumesRef = collection(db, 'users', userId, 'resumes');
    const resumesSnap = await getDocs(resumesRef);
    
    const resumes = [];
    resumesSnap.forEach(doc => {
      resumes.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    console.log(`✅ Found ${resumes.length} resumes`);
    return resumes;
  } catch (error) {
    console.error('❌ Error listing resumes:', error);
    throw error;
  }
};

/**
 * Update resume metadata (title, etc.) without updating data
 * @param {string} userId - User ID
 * @param {string} resumeId - Resume document ID
 * @param {object} metadata - Metadata to update
 * @returns {Promise<void>}
 */
export const updateResumeMetadata = async (userId, resumeId, metadata) => {
  try {
    console.log('📝 Updating resume metadata:', { userId, resumeId });
    
    const resumeRef = doc(db, 'users', userId, 'resumes', resumeId);
    
    await updateDoc(resumeRef, {
      ...metadata,
      updatedAt: serverTimestamp()
    });
    
    console.log('✅ Metadata updated successfully');
  } catch (error) {
    console.error('❌ Error updating metadata:', error);
    throw error;
  }
};

/**
 * Upload and parse resume
 * @param {File} file - Resume file (PDF/DOCX)
 * @param {string} userId - User ID
 * @returns {Promise<object>} Parsed resume data with ID
 */
export const uploadAndParseResume = async (file, userId) => {
  try {
    console.log('📤 Uploading and parsing resume...');
    console.log(`   File: ${file.name}`);
    console.log(`   Size: ${(file.size / 1024).toFixed(2)} KB`);
    console.log(`   Type: ${file.type}`);

    // Validate file type
    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword'
    ];

    if (!validTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload a PDF or DOCX file.');
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      throw new Error('File size exceeds 10MB limit.');
    }

    // Create FormData
    const formData = new FormData();
    formData.append('resume', file);
    formData.append('userId', userId);

    // Send to backend
    const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';
    const response = await fetch(`${API_BASE_URL}/api/ats/upload-and-parse`, {
      method: 'POST',
      body: formData
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Failed to upload and parse resume');
    }

    console.log('✅ Resume uploaded and parsed successfully');
    console.log(`   Parsed Data ID: ${result.data.parsedDataId}`);
    console.log(`   Sections found:`, result.data.sectionsFound);

    return result.data;

  } catch (error) {
    console.error('❌ Error uploading/parsing resume:', error);
    throw error;
  }
};

/**
 * Get parsed resume data from Firestore
 * @param {string} parsedDataId - Parsed data document ID
 * @returns {Promise<object>} Parsed resume data
 */
export const getParsedResumeData = async (parsedDataId) => {
  try {
    console.log('📂 Loading parsed resume data:', parsedDataId);
    
    const parsedDataRef = doc(db, 'uploadedResumes', parsedDataId);
    const parsedDataSnap = await getDoc(parsedDataRef);
    
    if (parsedDataSnap.exists()) {
      const data = parsedDataSnap.data();
      console.log('✅ Parsed data loaded successfully');
      return {
        id: parsedDataSnap.id,
        ...data
      };
    } else {
      console.warn('⚠️ Parsed data not found');
      return null;
    }
  } catch (error) {
    console.error('❌ Error loading parsed data:', error);
    throw error;
  }
};
